import queryString from "query-string";
import { AccountingUtils } from "revlock-accounting";
import UserActions from "redux/actions/user.actions";
import * as ChargebeeUtils from "utils/ChargebeeUtils";
import {
    getCurrentServiceProfile,
    parseOrgIdFromAppPath,
    ensureOrgProfile
} from "utils/WebUtils";

function resolveOrg(userOrganizations, orgId) {
    return (
        orgId &&
        userOrganizations.find((o) => String(o.id) === String(orgId))
    );
}

function matchesServiceProfile(org, serviceProfile) {
    return org && (!org.profile || org.profile === serviceProfile);
}

/**
 * Known tenant from Billing return, deep link, or site-switcher upgradeRedirect.
 * Priority: ?clientId= → path org → upgradeRedirect cookie.
 */
export function resolveExplicitColdStartTarget(cookies) {
    const search =
        (typeof window !== "undefined" &&
            window.location &&
            window.location.search) ||
        "";
    const { clientId } = queryString.parse(search);
    const pathOrgId =
        typeof window !== "undefined" &&
        window.location &&
        window.location.pathname
            ? parseOrgIdFromAppPath(window.location.pathname)
            : null;
    const upgradeRedirectPath = cookies && cookies.get("upgradeRedirect");
    const upgradeRedirectOrgId = upgradeRedirectPath
        ? parseOrgIdFromAppPath(upgradeRedirectPath)
        : null;

    if (clientId) {
        return { orgId: String(clientId), source: "clientId" };
    }
    if (pathOrgId) {
        return { orgId: String(pathOrgId), source: "pathOrgId" };
    }
    if (upgradeRedirectOrgId) {
        return { orgId: upgradeRedirectOrgId, source: "upgradeRedirect" };
    }
    return null;
}

/**
 * Ambiguous cold-start list: cookie → test org → remaining, same SPA profile only.
 */
export function buildSameProfileColdStartCandidates(
    userOrganizations,
    cookieOrgId,
    serviceProfile
) {
    const candidates = [];
    const seen = new Set();
    const pushUnique = (org) => {
        if (!matchesServiceProfile(org, serviceProfile) || !org || !org.id) {
            return;
        }
        const key = String(org.id);
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        candidates.push(org);
    };

    if (cookieOrgId) {
        pushUnique(resolveOrg(userOrganizations, cookieOrgId));
    }
    pushUnique(
        userOrganizations.find((o) => AccountingUtils.isTestId(o.id))
    );
    userOrganizations.forEach((org) => pushUnique(org));
    return candidates;
}

/**
 * Apply org on this SPA, or hard-nav via ensureOrgProfile when profiles differ.
 * Cross-profile Redux apply is what caused a/b redirect loops.
 */
export async function tryApplyOrgForColdStart(org, serviceProfile, opts) {
    if (!org) {
        return false;
    }
    if (!matchesServiceProfile(org, serviceProfile)) {
        console.log(
            `[SSO cold-start] org ${org.id} profile ${org.profile} != SPA ${serviceProfile}, ensureOrgProfile`
        );
        await ensureOrgProfile(org.id, {
            orgProfile: org.profile,
            url: `/${org.id}/dashboards`
        });
        return false;
    }
    return UserActions.setCurrentOrganization(org, undefined, opts);
}

/**
 * SSO-active cold start only (caller already probed enforcementActive).
 * Explicit target → one evaluate; else same-profile walk.
 */
export async function selectOrganizationForSsoColdStart({
    userOrganizations,
    cookies,
    hasCurrentOrg
}) {
    const serviceProfile = getCurrentServiceProfile();
    const explicit = resolveExplicitColdStartTarget(cookies);

    if (explicit) {
        const explicitOrg = resolveOrg(userOrganizations, explicit.orgId);
        console.log(
            `[SSO cold-start] explicit target ${explicit.orgId} from ${explicit.source}`
        );
        if (explicitOrg) {
            const ok = await tryApplyOrgForColdStart(explicitOrg, serviceProfile, {
                skipBillingRedirect: false
            });
            console.log(
                `[SSO cold-start] explicit target ${explicit.orgId} allowed=${ok}`
            );
        } else {
            console.log(
                `[SSO cold-start] explicit target ${explicit.orgId} not in user org list`
            );
        }
        return;
    }

    const candidates = buildSameProfileColdStartCandidates(
        userOrganizations,
        cookies && cookies.get("orgId"),
        serviceProfile
    );

    console.log(
        `[SSO cold-start] ambiguous walk, ${candidates.length} same-profile candidate(s) on SPA ${serviceProfile}`
    );

    for (let i = 0; i < candidates.length; i++) {
        if (hasCurrentOrg && hasCurrentOrg()) {
            return;
        }
        const last = i === candidates.length - 1;
        const ok = await tryApplyOrgForColdStart(candidates[i], serviceProfile, {
            skipBillingRedirect: !last
        });
        if (ok) {
            console.log(
                `[SSO cold-start] ambiguous walk selected org ${candidates[i].id}`
            );
            return;
        }
    }
}

/**
 * When SSO enforcement is on for this org and profiles differ, hard-nav to the
 * correct SPA instead of applying the org in Redux (avoids a/b loops + 426).
 * Returns true if caller may proceed with setCurrentOrganization.
 */
export async function canApplyOrgOnCurrentSpa(org) {
    if (!org || !org.id || !ChargebeeUtils.isChargebeeUser()) {
        return true;
    }
    const enforcementOn = await UserActions.isSsoEnforcementActiveCached([
        org
    ]);
    const serviceProfile = getCurrentServiceProfile();
    if (enforcementOn && org.profile && org.profile !== serviceProfile) {
        console.log(
            `[SSO profile-gate] org ${org.id} profile ${org.profile} != SPA ${serviceProfile}, ensureOrgProfile`
        );
        ensureOrgProfile(org.id, {
            orgProfile: org.profile,
            url:
                (typeof window !== "undefined" &&
                    window.location &&
                    window.location.pathname + window.location.search) ||
                `/${org.id}/dashboards`
        });
        return false;
    }
    return true;
}
